import React, { useState, useEffect } from 'react';
import { Award, Plus, Trash2, X, Star, CheckCircle } from 'lucide-react';
import { employeeService } from '../../services/employeeService';

export default function EmployeeSkillsCard({ employeeUuid, canEdit = true }) {
  const [skillAssignments, setSkillAssignments] = useState([]);
  const [catalogSkills, setCatalogSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    skill_uuid: '',
    proficiency_level: 'intermediate',
    years_of_experience: 1,
    self_rating: 5,
    manager_rating: 5,
    verified: false
  });

  const fetchSkillAssignments = async () => {
    try {
      setLoading(true);
      const res = await employeeService.getEmployeeSkillAssignments(employeeUuid);
      setSkillAssignments(res.data || []);
    } catch (err) {
      console.error('Failed to load employee skill assignments', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogSkills = async () => {
    try {
      const res = await employeeService.getSkillsCatalog();
      setCatalogSkills(res.data || []);
    } catch (err) {
      console.error('Failed to load skills catalog', err);
    }
  };

  useEffect(() => {
    if (employeeUuid) {
      fetchSkillAssignments();
      fetchCatalogSkills();
    }
  }, [employeeUuid]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await employeeService.addEmployeeSkillAssignment(employeeUuid, formData);
      setIsModalOpen(false);
      fetchSkillAssignments();
      setFormData({
        skill_uuid: '',
        proficiency_level: 'intermediate',
        years_of_experience: 1,
        self_rating: 5,
        manager_rating: 5,
        verified: false
      });
    } catch (err) {
      console.error('Error assigning skill:', err.response?.data || err);
      alert('Failed to assign skill.');
    }
  };

  const handleDelete = async (assignmentUuid) => {
    if (!window.confirm('Are you sure you want to remove this skill from the employee?')) return;
    try {
      await employeeService.deleteEmployeeSkillAssignment(employeeUuid, assignmentUuid);
      fetchSkillAssignments();
    } catch (err) {
      console.error('Error removing skill assignment:', err);
      alert('Failed to remove skill.');
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 flex flex-col gap-4">
      
      {/* Header matching other components */}
      <div className="flex items-center justify-between border-b pb-2 mb-2">
        <h3 className="text-lg font-semibold text-gray-800">Employee Skills & Competencies</h3>
        
        {canEdit === true && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition cursor-pointer shadow-sm"
          >
            <Plus className="w-4 h-4" /> Assign Skill
          </button>
        )}
      </div>

      {/* Skills Grid */}
      {loading ? (
        <p className="text-xs text-gray-500 text-center py-4">Loading assigned skills...</p>
      ) : skillAssignments.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4">No skills assigned yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {skillAssignments.map((item) => (
            <div key={item.uuid} className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex flex-col justify-between gap-3 shadow-2xs relative">
              
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                      <Award className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                          {item.skill?.name || 'Skill'}
                        </h4>
                        {item.verified && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded text-[9px] font-bold uppercase mt-0.5">
                            <CheckCircle className="w-2.5 h-2.5" /> Verified
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 font-medium mt-0.5 uppercase">
                        Level: <span className="text-blue-600 font-semibold">{item.proficiency_level}</span> • Exp: {item.years_of_experience} yrs
                      </p>
                    </div>
                  </div>

                  {canEdit === true && (
                    <button 
                      onClick={() => handleDelete(item.uuid)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition cursor-pointer"
                      title="Remove Skill"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
                  <div className="flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                    <span className="font-semibold text-gray-800">Self Rating:</span> {item.self_rating}/10
                  </div>
                  {item.manager_rating && (
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-blue-500 fill-blue-500" />
                      <span className="font-semibold text-gray-800">Manager:</span> {item.manager_rating}/10
                    </div>
                  )}
                </div>
              </div>

            </div>
          ))}
        </div>
      )}

      {/* Assign Skill Modal */}
      {isModalOpen && canEdit === true && (
        <div className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-gray-200 overflow-hidden animate-in fade-in duration-200">
            
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h4 className="text-sm font-bold text-gray-900">Assign Skill to Employee</h4>
              <button onClick={() => setIsModalOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-3">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Select Skill *</label>
                <select 
                  name="skill_uuid" 
                  value={formData.skill_uuid} 
                  onChange={handleChange}
                  required
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600 bg-white"
                >
                  <option value="">-- Choose Skill from Catalog --</option>
                  {catalogSkills.map((catSkill) => (
                    <option key={catSkill.uuid} value={catSkill.uuid}>
                      {catSkill.name} ({catSkill.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Proficiency Level *</label>
                  <select 
                    name="proficiency_level" 
                    value={formData.proficiency_level} 
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600 bg-white capitalize"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Years of Exp *</label>
                  <input 
                    type="number" 
                    step="0.1" 
                    name="years_of_experience" 
                    value={formData.years_of_experience} 
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Self Rating (1-10)</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="10" 
                    name="self_rating" 
                    value={formData.self_rating} 
                    onChange={handleChange}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Manager Rating (1-10)</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="10" 
                    name="manager_rating" 
                    value={formData.manager_rating} 
                    onChange={handleChange}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-1">
                <input 
                  type="checkbox" 
                  name="verified" 
                  id="skill_verified"
                  checked={formData.verified} 
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="skill_verified" className="text-xs font-semibold text-gray-700 cursor-pointer">
                  Verified by Manager / HR
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-sm cursor-pointer"
                >
                  Assign Skill
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

    </div>
  );
}